import {openDeleteDialog} from 'platform/components';
import {match} from 'ts-pattern';

import {useDispatch} from 'react-redux';

import {isNil, head, mergeAll} from 'ramda';
import {isArray} from 'ramda-adjunct';

import {
  useDeleteServiceOrderIssueNoteMutation,
  usePatchServiceOrderIssueNotePartsReadyMutation,
} from '@dms/api/warehouse';
import {metadaWorkshopServiceOrderApi} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {warehouseRoutes} from '@dms/routes';
import {catchUnhandledDataGridActions, DmsDataGrid, handleApiError, logError} from '@dms/shared';

import {composePath, useNavigate} from 'shared/web';

import type {ActionCallback, QueryFilterObject} from 'features/eag-grid';

interface ServiceCaseOrderIssuesDataGridProps {
  orderId: string;
}

const PREDEFINED_COLUMNS = ['partsReady'] as const;
export function ServiceCaseOrderIssuesDataGrid(props: ServiceCaseOrderIssuesDataGridProps) {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [patchServiceOrderIssueNotePartsReady] = usePatchServiceOrderIssueNotePartsReadyMutation();
  const [deleteServiceOrderIssueNote] = useDeleteServiceOrderIssueNoteMutation();

  const queryModifier = (filter: QueryFilterObject) =>
    mergeAll([filter, {serviceOrderId: props.orderId}]);

  const actionCallback: ActionCallback<typeof PREDEFINED_COLUMNS> = ({
    actionKey,
    rowId,
    refreshData,
    getColumnData,
  }) => {
    const selectedRowId = isArray(rowId) ? head(rowId) : rowId;

    if (isNil(selectedRowId)) {
      return logError('Selected row id is undefined');
    }

    match(actionKey)
      .with('redirectDetail', 'edit', () => {
        navigate(
          composePath(warehouseRoutes.serviceOrderIssueDetailOverview, {
            params: {
              id: selectedRowId,
            },
            isSearchParamsPreserved: false,
          })
        );
      })
      .with('ready', 'remove-ready', async () => {
        const arePartsReady = getColumnData<boolean>('partsReady')?.value;

        if (isNil(arePartsReady)) {
          return logError('Parts ready is not defined');
        }

        await patchServiceOrderIssueNotePartsReady({
          serviceOrderIssueNoteId: selectedRowId,
          body: {partsReady: actionKey === 'ready'},
        })
          .unwrap()
          .then(() => refreshData())
          .catch(handleApiError);
      })
      .with('delete', () => {
        openDeleteDialog({
          text: i18n.t('entity.warehouse.actions.deleteEntity'),
          onConfirm: () =>
            deleteServiceOrderIssueNote({serviceOrderIssueNoteId: selectedRowId})
              .unwrap()
              .then(() =>
                dispatch(
                  metadaWorkshopServiceOrderApi.util.invalidateTags([
                    {type: 'partMovementsCount', id: props.orderId},
                  ])
                )
              )
              .then(() => refreshData())
              .catch(handleApiError),
        });
      })
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  return (
    <DmsDataGrid
      autoHeight
      gridCode="service-case-order-issues"
      data-testid="serviceOrderIssues"
      queryModifier={queryModifier}
      actionCallback={actionCallback}
      predefinedColumns={PREDEFINED_COLUMNS}
    />
  );
}
